'use client';

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getBrowserWindow } from '@/lib/utils/browser-window';
import { cancelWindowAnimationFrame, requestWindowAnimationFrame } from '@/lib/utils/window-timers';
type TypewriterOptions = {
  /** Approx ms per character (base). Smaller = faster. */
  msPerChar?: number;
  /** Extra pause after punctuation (.,!?:;). */
  punctuationPauseMs?: number;
  /** Extra pause after newline. */
  newlinePauseMs?: number;
  /** How many latest chars get the "fresh ink" subtle glow. */
  recentHighlightCount?: number;
  /** Add small randomness to timing (0..0.5 => up to ±50%). */
  jitter?: number;
  /** Respect users who prefer reduced motion by disabling animation. */
  respectReducedMotion?: boolean;
};
const DEFAULT_OPTIONS: Required<TypewriterOptions> = {
  msPerChar: 10,
  punctuationPauseMs: 70,
  newlinePauseMs: 110,
  recentHighlightCount: 8,
  jitter: 0.18,
  respectReducedMotion: true
};
const PUNCTUATION_PATTERN = /[.,!?;:]/;
export interface TypewriterState {
  done: boolean;
  prefersReducedMotion: boolean;
  recent: string;
  skip: () => void;
  stable: string;
  typed: string;
}
const toArray = (input: string): string[] => {
  const segments = input.match(/./gu);
  return segments ? [...segments] : [];
};
const isGraphemeSegmenter = (segmenter: Intl.Segmenter | undefined): segmenter is Intl.Segmenter & {
  segment: (input: string) => Iterable<{
    segment: string;
  }>;
} => Boolean(segmenter && typeof segmenter.segment === 'function');
export function useTypewriter(sourceText: string, options?: TypewriterOptions): TypewriterState {
  const {
    msPerChar,
    punctuationPauseMs,
    newlinePauseMs,
    recentHighlightCount,
    jitter,
    respectReducedMotion
  } = {
    ...DEFAULT_OPTIONS,
    ...options
  } satisfies Required<TypewriterOptions>;
  const reducedMotionPreferred = useMemo(() => {
    if (!respectReducedMotion) {
      return false;
    }
    const win = getBrowserWindow();
    return Boolean(win?.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  }, [respectReducedMotion]);
  const prefersReducedMotion = useMemo(() => {
    if (reducedMotionPreferred) {
      return true;
    }
    const win_0 = getBrowserWindow();
    return !win_0 || typeof win_0.requestAnimationFrame !== 'function' || typeof win_0.cancelAnimationFrame !== 'function';
  }, [reducedMotionPreferred]);
  const segmenter = useMemo(() => {
    if (typeof Intl === 'undefined') {
      return;
    }
    try {
      return new Intl.Segmenter(undefined, {
        granularity: 'grapheme'
      });
    } catch {
      return;
    }
  }, []);
  const toGraphemes = useMemo(() => {
    if (isGraphemeSegmenter(segmenter)) {
      return (value: string): string[] => Array.from(segmenter.segment(value), entry => entry.segment);
    }
    return toArray;
  }, [segmenter]);
  const queueReference = useRef<string[]>([]);
  const seenCountReference = useRef<number>(0);
  const typedReference = useRef<string>('');
  const recentBufferReference = useRef<string[]>([]);
  const rafReference = useRef<number | null>(null);
  const [, setFrame] = useState(0);
  const accumulatorReference = useRef<number>(0);
  const lastTimestampReference = useRef<number | null>(null);
  const updateState = useCallback(() => {
    setFrame(previous => previous + 1);
  }, []);
  const cancelScheduledTick = useCallback(() => {
    if (rafReference.current === undefined) {
      return;
    }
    cancelWindowAnimationFrame(rafReference.current);
    rafReference.current = null;
  }, []);
  const computeDelayForChar = useCallback((char: string): number => {
    if (prefersReducedMotion) {
      return 0;
    }
    let delay = msPerChar;
    if (char === ' ') {
      delay *= 0.35;
    } else if (char === '\n') {
      delay += newlinePauseMs;
    } else if (PUNCTUATION_PATTERN.test(char)) {
      delay += punctuationPauseMs;
    }
    if (jitter > 0) {
      const randomOffset = (Math.random() - 0.5) * 2 * jitter;
      delay *= Math.max(0.5, 1 + randomOffset);
    }
    return Math.max(0, delay);
  }, [jitter, msPerChar, newlinePauseMs, prefersReducedMotion, punctuationPauseMs]);
  const revealCharacter = useCallback((char_0: string) => {
    typedReference.current += char_0;
    seenCountReference.current += 1;
    recentBufferReference.current.push(char_0);
    if (recentBufferReference.current.length > recentHighlightCount) {
      recentBufferReference.current.shift();
    }
  }, [recentHighlightCount]);
  const drainQueue = useCallback(() => {
    let revealed = 0;
    while (queueReference.current.length > 0) {
      const next = queueReference.current[0];
      if (typeof next !== 'string') {
        queueReference.current.shift();
        continue;
      }
      const delay_0 = computeDelayForChar(next);
      if (delay_0 > 0 && accumulatorReference.current < delay_0) {
        break;
      }
      if (delay_0 > 0) {
        accumulatorReference.current -= delay_0;
      }
      queueReference.current.shift();
      revealCharacter(next);
      revealed += 1;
    }
    return revealed;
  }, [computeDelayForChar, revealCharacter]);
  const flushQueue = useCallback(() => {
    let revealed_0 = 0;
    while (queueReference.current.length > 0) {
      const next_0 = queueReference.current.shift();
      if (typeof next_0 !== 'string') {
        continue;
      }
      revealCharacter(next_0);
      revealed_0 += 1;
    }
    accumulatorReference.current = 0;
    lastTimestampReference.current = null;
    return revealed_0;
  }, [revealCharacter]);
  const tick = useCallback((timestamp: number) => {
    const previousTimestamp = lastTimestampReference.current;
    const delta = previousTimestamp == null ? 0 : timestamp - previousTimestamp;
    lastTimestampReference.current = timestamp;
    accumulatorReference.current += delta;
    const revealed_1 = drainQueue();
    if (revealed_1 > 0) {
      updateState();
    }
    if (queueReference.current.length > 0) {
      const handle = requestWindowAnimationFrame(tick);
      if (handle === null) {
        const flushed = flushQueue();
        if (flushed > 0) {
          updateState();
        }
        rafReference.current = null;
      } else {
        rafReference.current = handle;
      }
    } else {
      rafReference.current = null;
      accumulatorReference.current = 0;
    }
  }, [drainQueue, flushQueue, updateState]);
  useLayoutEffect(() => {
    const graphemes = toGraphemes(sourceText);
    if (graphemes.length < seenCountReference.current) {
      cancelScheduledTick();
      seenCountReference.current = 0;
      queueReference.current = [...graphemes];
      typedReference.current = '';
      recentBufferReference.current = [];
      accumulatorReference.current = 0;
      lastTimestampReference.current = null;
      updateState();
      return;
    }
    const pending = graphemes.slice(seenCountReference.current);
    if (pending.length === 0) {
      return;
    }
    queueReference.current.push(...pending);
    seenCountReference.current = graphemes.length;
    if (rafReference.current === undefined) {
      lastTimestampReference.current = null;
      const handle_0 = requestWindowAnimationFrame(tick);
      if (handle_0 === null) {
        const flushed_0 = flushQueue();
        if (flushed_0 > 0) {
          updateState();
        }
      } else {
        rafReference.current = handle_0;
      }
    }
  }, [cancelScheduledTick, flushQueue, sourceText, tick, toGraphemes, updateState]);
  useEffect(() => () => {
    cancelScheduledTick();
  }, [cancelScheduledTick]);
  const typed = typedReference.current;
  const recent = recentBufferReference.current.join('');
  const stable = typed.slice(0, Math.max(0, typed.length - recent.length));
  const done = typed.length === sourceText.length;
  const skip = useCallback(() => {
    cancelScheduledTick();
    queueReference.current = [];
    typedReference.current = sourceText;
    const graphemes_0 = toGraphemes(sourceText);
    recentBufferReference.current = graphemes_0.slice(-recentHighlightCount);
    seenCountReference.current = graphemes_0.length;
    accumulatorReference.current = 0;
    lastTimestampReference.current = null;
    updateState();
  }, [cancelScheduledTick, recentHighlightCount, sourceText, toGraphemes, updateState]);
  return {
    typed,
    stable,
    recent,
    done,
    skip,
    prefersReducedMotion
  };
}